/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { AutocompleteItemMapper, AutocompleteItemWithDescription, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';

import { Observable } from 'rxjs';

import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import {
  HousePlanBulkUpdateAutocompleteMapper,
  HousePlanBulkUpdateMapper,
} from './mappers/house-plan-bulk-update.mapper';
import { HousePlanDetailsMapper, HousePlanGetTopsRequestMapper } from './mappers/house-plan-details.mapper';
import { HousePlanFilterMapper, HousePlanListItemMapper } from './mappers/house-plan-list-item.mapper';
import { HousePlanBulkUpdateAutocompleteDto, HousePlanBulkUpdateDto } from './models/house-plan-bulk.dto';
import { HousePlanDetailsDto, HousePlanTopListDto } from './models/house-plan-details.dto';
import { HousePlanFilterDto } from './models/house-plan-filter.dto';
import { HousePlanListInitDto } from './models/house-plan-list-init.dto';
import { HousePlanListItemDto } from './models/house-plan-list-item.dto';

@Injectable()
export class HousePlanRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  create(housePlan: HousePlanDetailsDto): Observable<HousePlanDetailsDto> {
    return this.httpPost(
      `${this.bp}/house-plans`,
      housePlan,
      optimisticLockingOf(HousePlanDetailsMapper));
  }

  update(housePlan: HousePlanDetailsDto): Observable<HousePlanDetailsDto> {
    return this.httpPut(`${this.bp}/house-plans/${housePlan.housePlanId}`, housePlan, optimisticLockingOf(HousePlanDetailsMapper));
  }

  remove(housePlan: HousePlanDetailsDto): Observable<any> {
    return this.httpDelete(
      `${this.bp}/house-plans/${housePlan.housePlanId}`,
      housePlan,
      optimisticLockingOf(HousePlanDetailsMapper));
  }

  get(id: ID): Observable<HousePlanDetailsDto> {
    return this.httpGet(`${this.bp}/house-plans/${id}`, optimisticLockingOf(HousePlanDetailsMapper));
  }

  getListInit(): Observable<HousePlanListInitDto> {
    return this.httpGet(`${this.bp}/house-plans/init`, identity());
  }

  findAllAsDataSource(filter: HousePlanFilterDto): SlicedDataSource<HousePlanListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/house-plans/list`,
      filter,
      HousePlanFilterMapper,
      arrayOf(HousePlanListItemMapper));
  }

  // tslint:disable-next-line:max-line-length
  findAllAsAutocompleteDataSource(filter: Partial<HousePlanFilterDto>): SlicedDataSource<AutocompleteItemWithDescription> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/house-plans/list`,
      { autocomplete: true, ...filter },
      identity(),
      arrayOf(AutocompleteItemMapper));
  }

  findAllAsBulkUpdateDataSource(filter: HousePlanFilterDto): SlicedDataSource<HousePlanBulkUpdateDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/house-plans/bulk-update`,
      filter,
      void 0,
      arrayOf(HousePlanBulkUpdateMapper));
  }

  findAllAsFinderDataSource(filter: HousePlanFilterDto): SlicedDataSource<HousePlanBulkUpdateDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/house-plans/finder`,
      filter,
      identity(),
      arrayOf(HousePlanBulkUpdateMapper));
  }

  // tslint:disable-next-line:max-line-length
  findAllAsBulkUpdateAutocompleteDataSource(filter: HousePlanFilterDto): SlicedDataSource<HousePlanBulkUpdateAutocompleteDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/house-plans/bulk-update`,
      filter,
      void 0,
      arrayOf(HousePlanBulkUpdateAutocompleteMapper));
  }

  activate(id: ID): Observable<HousePlanListItemDto> {
    return this.httpPut(`${this.bp}/house-plans/undelete/${id}`, void 0, optimisticLockingOf(HousePlanListItemMapper));
  }

  deactivateOrDelete(id: ID, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/house-plans/${id}`, checkUsage, CheckUsageMapper);
  }

  checkUsage(housePlan: HousePlanListItemDto): Observable<CheckUsageDto> {
    const { housePlanId, version } = housePlan;
    return this.httpGet(
      `${this.bp}/house-plans/check-usage/${housePlanId}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  updateHousePlanTops(housePlanId: ID, dto: HousePlanTopListDto): Observable<HousePlanTopListDto> {
    return this.httpPut(`${this.bp}/house-plans/${housePlanId}/take-off-params`, dto, HousePlanGetTopsRequestMapper);
  }

  findAllHousePlanTops(housePlanId: ID): Observable<HousePlanTopListDto> {
    return this.httpGet(`${this.bp}/house-plans/${housePlanId}/take-off-params`, HousePlanGetTopsRequestMapper);
  }
}
